import {
  Assignment as AssignmentIcon,
  Build as BuildIcon,
  EventNote as EventNoteIcon,
} from '@mui/icons-material';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import React, { useEffect } from 'react';
import EmailIcon from '@mui/icons-material/Email';
import { Images } from '@/assets/images';

import ManageApplications from './applications/ManageApplications';
import Admin_ManageApplicationCycles from './applicationCycle/ManageApplicationCycles';
import ManageEmails from './emails/ManageEmails';
import ManageSiteTools from './siteTools/ManageSiteTools';

const drawerWidth = 240;

type AdminComponentConfig = {
  text: string;
  icon: React.ReactNode;
};

const AdminComponents: Record<string, AdminComponentConfig> = {
  ApplicationCycle: { text: 'Application Cycles', icon: <EventNoteIcon /> },
  Applications: { text: 'Applications', icon: <AssignmentIcon /> },
  Emails: { text: 'Emails', icon: <EmailIcon /> },
  SiteTools: { text: 'Site Tools', icon: <BuildIcon /> },
};

const AdminHome: React.FC = () => {
  const theme = useTheme();

  const [selectedComponent, setSelectedComponent] = React.useState<string>(
    AdminComponents.ApplicationCycle.text
  );

  useEffect(() => {
    document.title = 'Admin';
  }, []);

  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <Drawer
        variant='permanent'
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          marginTop: '120px',
          p: 2,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            overflowX: 'hidden',
          },
        }}
      >
        <List>
          <ListItem sx={{ minHeight: '75px' }}>
            <ListItemIcon sx={{ minWidth: 56 }}>
              <img height='30px' src={Images.ArborDayLogoWide} alt='logo' />
            </ListItemIcon>
          </ListItem>

          {Object.keys(AdminComponents).map((key) => {
            const item = AdminComponents[key];
            const isSelected = selectedComponent === item.text;

            return (
              <ListItem key={key} disablePadding sx={{ maxWidth: '240px', mx: '2px' }}>
                <ListItemButton
                  selected={isSelected}
                  onClick={() => setSelectedComponent(item.text)}
                  sx={{
                    // Selected state
                    '&.Mui-selected': {
                      color: theme.palette.forest.main,
                      borderRight: `1px solid ${theme.palette.forest.main}`,
                      '& .MuiSvgIcon-root': {
                        color: theme.palette.forest.main,
                      },
                      '& .MuiListItemText-primary': {
                        fontWeight: theme.typography.fontWeightBold,
                      },
                    },

                    // Hover state
                    '&:hover': {
                      color: theme.palette.forestShade.main,
                      borderRight: `1px solid ${theme.palette.forestShade.main}`,
                      '& .MuiSvgIcon-root': {
                        color: theme.palette.forestShade.main,
                      },
                    },

                    // Selected + hover (optional, but makes it consistent)
                    '&.Mui-selected:hover': {
                      color: theme.palette.forestShade.main,
                      borderRight: `1px solid ${theme.palette.forestShade.main}`,
                      '& .MuiSvgIcon-root': {
                        color: theme.palette.forestShade.main,
                      },
                    },
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Drawer>

      <Box
        sx={{ flexGrow: 1, display: 'flex', p: theme.spacing(1), minHeight: 0, overflow: 'hidden' }}
      >
        {selectedComponent === AdminComponents.ApplicationCycle.text && (
          <Admin_ManageApplicationCycles />
        )}

        {selectedComponent === AdminComponents.Applications.text && <ManageApplications />}
        {selectedComponent === AdminComponents.Emails.text && <ManageEmails />}
        {selectedComponent === AdminComponents.SiteTools.text && <ManageSiteTools />}
      </Box>
    </Box>
  );
};

export default AdminHome;
